import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import StepForm from '../../../../../../features/components/Forms/StepForm'
import NameStep from '../Steps/NameStep'
import LinkStep from '../Steps/LinkStep'
import TextStep from '../Steps/TextStep'
import MediaFileStep from '../Steps/MediaFileStep'
import HeadlineStep from '../Steps/HeadlineStep'
import CallToActionStep from '../Steps/ActionStep'
import FacebookPageStep from '../Steps/FacebookPageStep'
import StatusStep from '../Steps/StatusStep'
import UrlTagsStep from '../Steps/UrlTagsStep'
import CustomizedMediaStep from '../Steps/CustomizedMediaStep'
import CustomizedMediaQuestionStep from '../Steps/CustomizedMediaQuestionStep'
import WarningStep from '../Steps/WarningStep'
import InstantFormSelectionStep from '../Steps/InstantFormSelectionStep'

import { useCreateFacebookAd } from '../hooks/useCreateFacebookAd'
import getShowUrlTagsStep from '../helpers/getShowUrlTagsStep'

import { accessibleSocialPagesSelector } from '../../../../../../modules/selectors/socialAccounts'

import {
  CALL_TO_ACTION,
  CLICK_THROUGH_LINK,
  CUSTOMIZED_MEDIA_OPTION,
  FORMAT_SINGLE,
  UPLOAD_CUSTOMIZED_MEDIA
} from '../../../fields'
import { AdFacebookCreateContext } from '../../../../formsContexts/AdFacebookCreateFormContextProvider'
import { LEAD_GENERATION } from '../../../../../../constants/campaigns'
import { PAGE_LIKES } from '../../../../../../constants/reusableObjectives'

const SingleMediaFileRoute = ({
  FormRouteSelector,
  eventSourceStep,
  isInternalAdCreation,
  selectedApplicationId,
  selectedPixelPassedFromOutside,
  allowSendPixel,
  ...formProps
}) => {
  const facebookPages = useSelector(accessibleSocialPagesSelector)
  const { preselectedValues, lineItem } = useContext(AdFacebookCreateContext)
  const { objective: lineItemObjective } = lineItem

  const { isAdSetEndDateInThePast, showAsStory, placementPositions, purifiedFormik, isDomainValidating } =
    useCreateFacebookAd({
      isInternalAdCreation,
      selectedApplicationId,
      selectedPixelPassedFromOutside,
      allowSendPixel,
      formatOption: FORMAT_SINGLE
    })

  const { values } = purifiedFormik

  const showCustomizedMediaQuestionStep =
    // hide customized media question step for lineItems with objective "page_likes"
    lineItemObjective !== PAGE_LIKES &&
    placementPositions &&
    (placementPositions.includes('facebook_story') ||
      placementPositions.includes('instagram_story') ||
      placementPositions.includes('instagram_reels') ||
      !placementPositions.length)

  const showCustomizedMediaStep =
    showCustomizedMediaQuestionStep && values[CUSTOMIZED_MEDIA_OPTION] === UPLOAD_CUSTOMIZED_MEDIA

  const showUrlTagsStep = getShowUrlTagsStep(values, lineItemObjective)

  const steps = useMemo(
    () => [
      {
        component: <WarningStep />,
        show: isAdSetEndDateInThePast
      },
      {
        component: FormRouteSelector,
        show: !showAsStory
      },
      {
        component: <NameStep />,
        show: true
      },
      {
        component: <LinkStep />,
        show: !preselectedValues[CLICK_THROUGH_LINK] && lineItemObjective !== LEAD_GENERATION
      },
      {
        component: <FacebookPageStep />,
        show: facebookPages.length !== 1
      },
      {
        component: <InstantFormSelectionStep />,
        show: lineItemObjective === LEAD_GENERATION
      },
      {
        component: <TextStep />,
        show: true
      },
      {
        component: <MediaFileStep />,
        show: true
      },
      {
        component: <HeadlineStep />,
        // We don't show HeadlineStep for lineItems with objective "page_likes"
        show: lineItemObjective !== PAGE_LIKES
      },
      {
        component: <CallToActionStep />,
        show: !preselectedValues[CALL_TO_ACTION]
      },
      {
        component: <CustomizedMediaQuestionStep />,
        show: showCustomizedMediaQuestionStep
      },
      {
        component: <CustomizedMediaStep />,
        show: showCustomizedMediaStep
      },
      eventSourceStep,
      {
        component: <UrlTagsStep selectedApplicationId={selectedApplicationId} />,
        show: showUrlTagsStep
      },
      {
        component: <StatusStep />,
        show: true
      }
    ],
    [
      FormRouteSelector,
      eventSourceStep,
      showCustomizedMediaQuestionStep,
      showCustomizedMediaStep,
      showAsStory,
      isAdSetEndDateInThePast,
      facebookPages,
      showUrlTagsStep,
      preselectedValues,
      lineItemObjective,
      selectedApplicationId
    ]
  )

  return <StepForm steps={steps} formik={purifiedFormik} {...formProps} isSubmitDataLoading={isDomainValidating} />
}

export default SingleMediaFileRoute
